---
title: "Resume parsing in Chosen"
description: "How resume parsing works in Chosen: an LLM extracts name, contact, work history, education, and skills from an uploaded resume into structured candidate fields."
category: "candidates"
keywords:
  - "resume parsing"
  - "resume parser ATS"
  - "extract resume fields"
  - "parse resume to candidate"
  - "replace a candidate's resume"
schema: "HowTo"
nav:
  section: "guides"
  order: 30
  parent: "/guides/candidates"
---

When you upload a resume, Chosen runs it through an LLM that pulls out structured fields and writes them onto the candidate record. You get a filled-in profile instead of a file you have to read. It's right most of the time, and you can fix anything it isn't.

## What gets extracted

The parser reads the resume text and fills in:

- **Name, email, phone** — the contact basics.
- **Current title and company** — where they are now.
- **Work history** — past roles with companies and dates.
- **Education** — degrees and schools.
- **Skills** — what they list.

Those land in the standard candidate fields. Any [custom fields](/guides/candidates/custom-fields) you've set to auto-fill get extracted in the same pass — Chosen reads each field's description to know what to look for.

## How parsing runs

Parsing happens in the background, not while you wait. A resume moves through a few states:

- **Queued** — uploaded, waiting its turn.
- **Running** — the LLM is reading it.
- **Succeeded** — fields are written to the candidate.
- **Failed** — something went wrong; the candidate exists but its fields are empty.

For a single upload this is quick. For a bulk batch, candidates appear right away and their fields fill in as each resume finishes — the table shows a placeholder on cells still waiting.

## Fixing what it got wrong

The parser is good, not perfect. Creative job titles ("Chief Vibe Officer") and dense academic CVs are where it's most likely to miss.

<Steps>
  <li>Open the candidate's detail panel.</li>
  <li>Edit any field directly — your correction is what everyone sees from then on.</li>
  <li>To re-extract everything from the original file, use **Reparse**.</li>
</Steps>

Reparse re-runs the parser against the source resume. Reach for it after you've added a new auto-fill custom field, or when a search isn't finding a candidate you'd expect — a hand-edit fixes the visible field but a reparse refreshes everything Chosen searches on.

## Replacing a resume

A newer resume belongs on the candidate you already have, not on a second record. Open the candidate and use the upload button next to the download button — it says **Upload resume** when there's no file yet and **Replace resume** when there is. PDF and Word files both work.

The profile shows **Parsing the resume…** while it works, then fills in from the new file. The [match rating](/guides/candidates/match-rating) is re-scored for every job the candidate is in, since the old score read the old resume. An upload from a candidate's profile always stays on that candidate, even when someone else in your organization has the same file.

<Callout type="warning" title="Scanned and image-only PDFs barely parse">
Chosen parses **PDF and DOCX** resumes. A PDF that's really a photo of a page — scanned, or exported as an image — has no text to read, so it parses to almost nothing. If a candidate's fields come back empty, check whether you can select text in the file. If you can't, ask for a real document. [More on parsing failures](/troubleshooting/resume-parsing-failed).
</Callout>

## When it gets it wrong

Two non-obvious behaviors worth knowing:

- **The same file twice is silently deduped.** Upload a resume Chosen has already seen as a new candidate and it matches the file by content and reuses the existing candidate — no prompt, no duplicate. A *different* resume for the same person makes a second record. See [duplicates and merging](/guides/candidates/duplicates-and-merging).
- **Very long resumes lose their tail.** Parsing reads roughly the first 35,000 characters. A normal one- or two-page resume is nowhere near that. A fifteen-page CV is, and anything past the cutoff — often the oldest roles — won't be extracted.

Parsed data also feeds the [match rating](/guides/candidates/match-rating), so a clean parse makes that score more reliable too.
